<template>
  <div>
    <fieldset>
      <input
        type="hidden"
        :name="'dq-vehicle-customisation-b'"
        :value="currentSelectionKey"
        :data-vv-scope="validationScope"
        v-validate.continues="'dq-vehicle-customisation-b'"/>

      <input id="RangeId" name="rangeId" type="hidden" :value="searchQuery.rangeIds">

      <trimsComponent ref="trimComponent" v-if="showTrims"
        :ignoreDerivative="true"
        :allowNotSure="true"
        :trimLabel="'Trim <span>*</span>'"
        :validationScope="validationScope"
        :allowTrimBot="allowTrimBot"
        :trimsOrderedBy="trimsOrderedBy"
        @trimsSet="resetSelection()"/>

      <fieldset id="fuelType-wp-dq" class="tla-form-group" v-if="showFuelType" v-show="!hideFuelTypeIfOneOption">
        <label class="tla-form-group__label tla-form-group__label--required">
          {{fuelTypeLabel}} <span>*</span>
        </label>
        <div v-if="questionLabels[0]">
          <label class="tla-form-group__label"
            v-html="questionLabels[0]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in fuelTypeOptions"
            :itemName="item"
            :index="index"
            :key="`${item}-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'fuelType'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(fuelTypeOptions.length < 2) || item === searchQuery.fuelTypes"
            :itemText="item"
            :arrayLength="fuelTypeOptions.length"
            @fuelType="setFuelTypeChoice($event)">
          </form-touch-point>

          <form-touch-point v-if="showFuelTypeNotSure"
            :itemName="`notSure-fuelType`"
            :key="`notSure-fuelType-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'fuelType'"
            :collectionClassName="'not-sure'"
            :validationRules="''"
            :validationScope="validationScope"
            :itemText="`Not Sure`"
            @fuelType="setFuelTypeChoice(null, true)">
          </form-touch-point>
        </div>
        <span class="tla-form-group__message " :class="getFuelValidationClass('message')">
          Please select an option.
        </span>
      </fieldset>

      <fieldset id="transmission-wp-dq" class="tla-form-group" v-show="!hideTransmissionIfOneOption" v-if="showTransmission">
        <label class="tla-form-group__label tla-form-group__label--required">
          {{transmissionLabel}} <span>*</span>
        </label>
        <div v-if="questionLabels[1]">
          <label class="tla-form-group__label"
            v-html="questionLabels[1]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in transmissionOptions"
            :itemName="item"
            :index="index"
            :key="`${item}-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'transmission'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(transmissionOptions.length < 2) || item === searchQuery.transmissions"
            :itemText="item"
            :arrayLength="transmissionOptions.length"
            @transmission="setTransmissionChoice($event)">
          </form-touch-point>

          <form-touch-point v-if="showTransmissionNotSure"
            :itemName="`notSure-transmission`"
            :key="`notSure-transmission-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'transmission'"
            :collectionClassName="'not-sure'"
            :validationRules="''"
            :validationScope="validationScope"
            :itemText="`Not Sure`"
            @transmission="setTransmissionChoice(null, true)">
          </form-touch-point>
        </div>
        <span class="tla-form-group__message " :class="getTransmissionValidationClass('message')">
          Please select an option.
        </span>
      </fieldset>

      <fieldset id="engineSize-wp-dq" class="tla-form-group" v-show="!hideEngineIfOneOption" v-if="showEngineSize">
        <label class="tla-form-group__label tla-form-group__label--required">
          {{engineLabel}} <span>*</span>
        </label>
        <div v-if="questionLabels[2]">
          <label class="tla-form-group__label"
            v-html="questionLabels[2]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in engineSizeOptions"
            :itemName="`${item}-engineSize`"
            :index="index"
            :key="`${item}-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'engineSize'"
            :collectionClassName="'engine-size'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(engineSizeOptions.length < 2) || item === searchQuery.engineSize"
            :itemText="`${formatEngineSizeText(item)}L`"
            :arrayLength="engineSizeOptions.length"
            @engineSize="setEngineSizeChoice($event)">
          </form-touch-point>

          <form-touch-point v-if="showEngineSizeNotSure"
            :itemName="`notSure-engineSize`"
            :key="`notSure-engineSize-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'engineSize'"
            :collectionClassName="'not-sure'"
            :validationRules="''"
            :validationScope="validationScope"
            :itemText="`Not Sure`"
            @engineSize="setEngineSizeChoice(null, true)">
          </form-touch-point>
        </div>
        <span class="tla-form-group__message " :class="getEngineValidationClass('message')">
          Please select an option.
        </span>
      </fieldset>

      <fieldset id="doors-wp" class="tla-form-group" v-if="showDoors">
        <label class="tla-form-group__label tla-form-group__label--required">
          Doors <span>*</span>
        </label>
        <div v-if="questionLabels[3]">
          <label class="tla-form-group__label"
            v-html="questionLabels[3]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in doorOptions"
            :itemName="`${item}-doors`"
            :itemId="item"
            :collectionClassName="'doors'"
            :index="index"
            :key="`${item}-${trim}-${bodyStyle}-${modelId}`"
            :collectionName="'selectedDoors'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(doorOptions.length < 2) || item === searchQuery.doors"
            :itemText="item"
            :arrayLength="doorOptions.length"
            @selectedDoors="setDoorChoice($event)">
          </form-touch-point>
        </div>
        <span class="tla-form-group__message " :class="getDoorValidationClass('message')">
          Please select an option.
        </span>
      </fieldset>
      <DetailedColourComponent
        v-if="showDetailedColours"
        :validationScope="validationScope"
        :basicColourLabel="basicColourLabel"
        :colourRestrictionText="colourRestrictionText"/>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { Validator } from 'vee-validate';
import {
  Component, Prop, Vue, Watch, Inject, Ref, Provide
} from 'vue-property-decorator';
import SearchQuery from '@/models/SearchQuery';
import { namespace } from 'vuex-class';
import TrimsOrderedBy from '@/enums/TrimsOrderedBy';
import { validationService } from '../../services/ValidationService';
import FormTouchPointComponent from '../ui/FormTouchPointComponent.vue';
import IDerivative from '../../models/vehicle/interfaces/derivative.d';
import ExtraData from '../../models/ExtraData';
import VehicleTrimComponent from './VehicleTrimComponent.vue';
import DetailedColourComponent from './DetailedColourComponent.vue';

// Custom vee-validate
Validator.extend('dq-vehicle-customisation-b', {
  getMessage: (field: string) => 'Please select a vehicle',
  validate: (value: string) => validationService.isDQVehicleCustomisationBDataSet()
});

const InitialSettingsModule = namespace('initialSettingsModule');

@Component({
  components: {
    'form-touch-point': FormTouchPointComponent,
    trimsComponent: VehicleTrimComponent,
    DetailedColourComponent
  }
})
export default class DQVehicleCustomisationComponentB extends Vue {
  @InitialSettingsModule.Getter public initialSettings;

  @Inject('$validator') public $validator!: Validator;

  @Provide() validate: Validator = this.$validator;

  @Prop({ default: 'Fuel Type' })
  public fuelTypeLabel!: string;

  @Prop({ default: 'Transmission' })
  public transmissionLabel!: string;

  @Prop({ default: 'Engine Size' })
  public engineLabel!: string;

  @Prop({ default: 'Pick your colour <span>*</span>' })
  public basicColourLabel!: string;

  @Prop({ default: 'Based on available manufacturer options' })
  public colourRestrictionText!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public allowTrimBot!: boolean;

  @Prop({ default: () => [] })
  public questionLabels!: string[];

  @Prop({ default: TrimsOrderedBy.Alphabetical })
  public trimsOrderedBy!: TrimsOrderedBy;

  @Ref()
  public readonly trimComponent!: VehicleTrimComponent;

  private fuelTypeOptions: string[] = [];

  private transmissionOptions: string[] = [];

  private engineSizeOptions: number[] = [];

  private fuelTypeNotSure: boolean = false;

  private transmissionNotSure: boolean = false;

  private engineSizeNotSure: boolean = false;

  public getFuelValidationClass(fieldType: string) {
    if (this.showTransmission) {
      return '';
    }

    return this.getValidationClass('dq-vehicle-customisation-b', fieldType);
  }

  public getTransmissionValidationClass(fieldType: string) {
    if (this.showEngineSize) {
      return '';
    }

    return this.getValidationClass('dq-vehicle-customisation-b', fieldType);
  }

  public getEngineValidationClass(fieldType: string) {
    if (this.showDoors) {
      return '';
    }

    return this.getValidationClass('dq-vehicle-customisation-b', fieldType);
  }

  public getDoorValidationClass(fieldType: string) {
    if (this.showDetailedColours) {
      return '';
    }

    return this.getValidationClass('dq-vehicle-customisation-b', fieldType);
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public get modelId(): number {
    return this.searchQuery.modelId !== null ? this.searchQuery.modelId : 0;
  }

  public get trim(): string {
    if (this.searchQuery.trims && this.searchQuery.trims.length > 0) {
      return this.searchQuery.trims[0];
    }

    const extraData: ExtraData = this.$store.getters['enquiryModule/extraData']
      .find((x: ExtraData) => x.keyName === 'trimNotSure');

    if (extraData) {
      return 'notSure';
    }

    return '';
  }

  public get bodyStyle(): string {
    if (this.searchQuery.bodystyles && this.searchQuery.bodystyles.length > 0) {
      return this.searchQuery.bodystyles[0];
    }

    const extraData: ExtraData = this.$store.getters['enquiryModule/extraData']
      .find((x: ExtraData) => x.keyName === 'bodyStyleNotSure');

    if (extraData) {
      return 'notSure';
    }

    return '';
  }

  public get doors(): string[] | null {
    return this.$store.getters['vehicleSearchModule/doors'];
  }

  public doorOptions: string[] | null = [];

  public setDoorOptions() {
    if (this.doors !== null && this.doors.length === 1) {
      this.setDoorChoice(this.doors[0]);
    }

    this.doorOptions = this.doors;
  }

  public get hideDoorsTouchpoint(): boolean {
    return this.doorOptions!.length <= 1;
  }

  public get derivatives(): IDerivative[] | null {
    return this.$store.getters['vehicleSearchModule/derivatives'];
  }

  public get derivative(): number | null {
    return this.searchQuery.derivativeId;
  }

  public set derivative(derivativeId: number | null) {
    let derivative: IDerivative | null = null;
    if (derivativeId !== null && this.derivatives !== null) {
      [derivative] = this.derivatives.filter((d) => d.derivativeId === derivativeId);
    }

    this.$store.dispatch('vehicleModule/submitDerivative', derivative);
  }

  public get searchQuery(): SearchQuery {
    return this.$store.getters['searchQueryModule/searchQuery'];
  }

  public get currentSelectionKey(): string {
    return this.$store.getters['vehicleModule/currentSelectionKey'];
  }

  public get showTrims(): boolean {
    return (this.bodyStyle.length > 0 || this.modelId > 0) && this.searchQuery.rangeIds !== null;
  }

  public get showFuelType(): boolean {
    return this.showTrims && (this.trim.length > 0 || (this.trimComponent && this.trimComponent.derivativesHaveNoTrims));
  }

  public get showTransmission(): boolean {
    return this.showFuelType && ((this.searchQuery.fuelTypes !== null && this.searchQuery.fuelTypes.length === 1) || this.fuelTypeNotSure);
  }

  public get showEngineSize(): boolean {
    return this.showTransmission && (this.searchQuery.transmissions !== null || this.transmissionNotSure);
  }

  public get showDoors(): boolean {
    return this.showEngineSize && (this.searchQuery.engineSize !== null || this.engineSizeNotSure);
  }

  public get showDetailedColours(): boolean {
    return this.showDoors && this.searchQuery.doors !== null;
  }

  public get showFuelTypeNotSure(): boolean {
    return this.fuelTypeOptions.length > 1;
  }

  public get showTransmissionNotSure() {
    return this.transmissionOptions.length > 1;
  }

  public get showEngineSizeNotSure() {
    return this.engineSizeOptions.length > 1;
  }

  public setFuelTypeOptions() {
    const fuelTypes = this.$store.getters['vehicleSearchModule/fuelTypes'];

    if (fuelTypes !== null && fuelTypes.length === 1) {
      this.setFuelTypeChoice(fuelTypes[0]);
    }

    this.fuelTypeOptions = fuelTypes;
  }

  public setTransmissionOptions() {
    const transmissions = this.$store.getters['vehicleSearchModule/transmissions'];
    if (transmissions !== null && transmissions.length === 1) {
      this.setTransmissionChoice(transmissions[0]);
    }

    this.transmissionOptions = transmissions;
  }

  public setEngineSizeOptions() {
    const engineSizes = this.$store.getters['vehicleSearchModule/engineSizes'];

    if (engineSizes !== null && engineSizes.length === 1) {
      this.setEngineSizeChoice(`${engineSizes[0]}`);
    }

    this.engineSizeOptions = engineSizes;
  }

  public formatEngineSizeText(value: number) {
    return value.toPrecision(2);
  }

  public async setFuelTypeChoiceValue(value: string | null, isNotSure: boolean = false) {
    if (isNotSure) {
      const extraData: ExtraData = new ExtraData('fuelTypeNotSure', 'true');
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'fuelTypeNotSure');
    }

    this.fuelTypeNotSure = isNotSure;
    await this.$store.dispatch('vehicleModule/submitFuelTypes', value ? [value] : value);
  }

  public async setTransmissionChoiceValue(value: string | null, isNotSure: boolean = false) {
    if (isNotSure) {
      const extraData: ExtraData = new ExtraData('transmissionNotSure', 'true');
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'transmissionNotSure');
    }

    this.transmissionNotSure = isNotSure;
    await this.$store.dispatch('vehicleModule/submitTransmission', value);
  }

  get hideFuelType(): boolean {
    return this.fuelTypeOptions.length <= 1;
  }

  get hideTransmission(): boolean {
    return this.transmissionOptions.length <= 1;
  }

  get hideEngine(): boolean {
    return this.engineSizeOptions.length <= 1;
  }

  get hideFuelTypeIfOneOption(): boolean {
    return this.initialSettings.hideFieldsWithOneOption && this.hideFuelType;
  }

  get hideTransmissionIfOneOption(): boolean {
    return this.initialSettings.hideFieldsWithOneOption && this.hideTransmission;
  }

  get hideEngineIfOneOption(): boolean {
    return this.initialSettings.hideFieldsWithOneOption && this.hideEngine;
  }

  public async setEngineSizeChoiceValue(value: string | null, isNotSure: boolean = false) {
    if (isNotSure) {
      const extraData: ExtraData = new ExtraData('engineSizeNotSure', 'true');
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'engineSizeNotSure');
    }

    this.engineSizeNotSure = isNotSure;
    const engineSize = value ? parseFloat(value) : null;
    await this.$store.dispatch('vehicleModule/submitEngineSize', engineSize);
    this.$store.dispatch('vehicleSearchModule/updateFilteredResults');
  }

  public async setDoorChoiceValue(value: string | null) {
    // We need to set this to null so that the colour control
    // resets itself
    await this.$store.dispatch('vehicleModule/submitDoors', null);

    const doors = value !== null ? parseInt(value, 10) : value;

    await this.$store.dispatch('vehicleModule/submitDoors', doors);
    this.$store.dispatch('vehicleSearchModule/updateFilteredResults');
  }

  public async setFuelTypeChoice(value: string | null, isNotSure: boolean = false) {
    this.resetFuelTypeChildOptions();
    await this.setFuelTypeChoiceValue(value, isNotSure);
    await this.$store.dispatch('vehicleSearchModule/updateFilteredResults');
    this.setTransmissionOptions();
  }

  public async setTransmissionChoice(value: string | null, isNotSure: boolean = false) {
    this.resetTransmissionChildOptions();
    this.setTransmissionChoiceValue(value, isNotSure);
    await this.$store.dispatch('vehicleSearchModule/updateFilteredResults');
    this.setEngineSizeOptions();
  }

  public async setEngineSizeChoice(value: string | null, isNotSure: boolean = false) {
    this.resetEngineSizeChildOptions();
    this.setEngineSizeChoiceValue(value, isNotSure);
    await this.$store.dispatch('vehicleSearchModule/updateFilteredResults');
    this.setDoorOptions();
  }

  public async setDoorChoice(value: string | null) {
    this.resetDoorChoiceChildOptions();
    await this.setDoorChoiceValue(value);
    this.$events.$emit('resetVehicleColours');
  }

  public resetDoorChoiceChildOptions() {
    this.$events.$emit('resetVehicleColours');
    this.derivative = null;
  }

  public resetFuelTypeChildOptions() {
    this.transmissionOptions = [];
    this.engineSizeOptions = [];
    this.doorOptions = [];

    this.setTransmissionChoiceValue(null);
    this.setEngineSizeChoiceValue(null);
    this.setDoorChoiceValue(null);
    this.derivative = null;
  }

  public resetTransmissionChildOptions() {
    this.engineSizeOptions = [];

    this.setEngineSizeChoiceValue(null);
    this.setDoorChoiceValue(null);
    this.derivative = null;
  }

  public resetEngineSizeChildOptions() {
    this.doorOptions = [];
    this.setDoorChoiceValue(null);
    this.derivative = null;
  }

  public async resetSelection() {
    this.fuelTypeOptions = [];
    this.resetFuelTypeChildOptions();
    await this.setFuelTypeChoiceValue(null);
    await this.$store.dispatch('vehicleSearchModule/updateFilteredResults');
    this.setFuelTypeOptions();
  }

  @Watch('currentSelectionKey')
  public validateSelectedOptions() {
    this.$validator.validateAll(this.validationScope);
  }

  public vehicleLevelChanged(moduleKey: string) {
    if (this.trimComponent && moduleKey.length === 0) {
      this.trimComponent.setTrimOptions();
    }
  }

  public created() {
    this.$events.on('vehicleLevelBodystyleSet', (moduleKey) => this.vehicleLevelChanged(moduleKey));
    this.$events.on('vehicleLevelSet', (moduleKey) => this.vehicleLevelChanged(moduleKey));
    this.setFuelTypeOptions();
  }
}
</script>
